import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, PlusIcon, TrashIcon, XMarkIcon, ChevronDownIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import './Integrations.css';

// ─── Platform config ──────────────────────────────────────────────────────────

const PLATFORMS = {
  facebook: {
    label: 'Facebook Messenger',
    icon: '💬',
    color: '#1877F2',
    bg: '#e7f0fd',
    oauthFlow: 'facebook',         // uses one-click OAuth
    channelIdLabel: 'Page ID',
    channelIdHelp: 'Your Facebook Page ID — shown in Facebook Page → About → bottom of the page.',
    accessTokenLabel: 'Page Access Token',
    accessTokenHelp: 'In Meta App Dashboard: Messenger → Settings → Access Tokens → Generate token next to your page. For a non-expiring token use a System User.',
    verifyTokenHelp: 'Invent any secret string (e.g. "my-secret-42"). Enter the exact same string in Meta App Dashboard → Messenger → Settings → Webhooks → Verify Token.',
  },
  whatsapp: {
    label: 'WhatsApp Business',
    icon: '📱',
    color: '#25D366',
    bg: '#e8fdf0',
    oauthFlow: 'whatsapp',         // uses Embedded Signup
    channelIdLabel: 'Phone Number ID',
    channelIdHelp: 'In Meta App Dashboard: WhatsApp → API Setup → the numeric Phone Number ID shown under your verified number.',
    accessTokenLabel: 'System Access Token',
    accessTokenHelp: 'Meta Business Suite → Business Settings → Users → System Users → Generate token with whatsapp_business_messaging + whatsapp_business_management. Do NOT use the temporary token in API Setup — it expires in 24 h.',
    verifyTokenHelp: 'Invent any secret string. Enter the same string in Meta App Dashboard → WhatsApp → Configuration → Webhook Verify Token.',
  },
  instagram: {
    label: 'Instagram DM',
    icon: '📸',
    color: '#E1306C',
    bg: '#fde8ef',
    oauthFlow: 'facebook',         // Instagram comes along with Facebook OAuth automatically
    channelIdLabel: 'Instagram Account ID',
    channelIdHelp: 'In Meta App Dashboard: Messenger → Settings → Instagram section → connect your account → the Account ID appears beneath it.',
    accessTokenLabel: 'Page Access Token',
    accessTokenHelp: 'Use the Page Access Token for the Facebook Page connected to your Instagram account (same token as Facebook Messenger).',
    verifyTokenHelp: 'Invent any secret string. Enter the same string in Meta App Dashboard → Messenger → Settings → Webhooks → Verify Token.',
  },
};

// ─── Manual connect modal (advanced / fallback) ───────────────────────────────

function ManualConnectModal({ platform, chatbotId, onClose, onConnected, existingChannel }) {
  const cfg = PLATFORMS[platform];
  const baseUrl = window.location.origin;
  const isEdit = !!existingChannel;

  const [form, setForm] = useState({
    channel_id:            existingChannel?.channel_id            || '',
    access_token:          '',   // never pre-fill — security
    webhook_verify_token:  existingChannel?.webhook_verify_token  || '',
    display_name:          existingChannel?.display_name          || '',
  });
  const [saving,  setSaving]  = useState(false);
  const [touched, setTouched] = useState(false);

  const set = (k, v) => { setTouched(true); setForm(prev => ({ ...prev, [k]: v })); };

  const handleClose = () => {
    if (touched && !window.confirm('You have unsaved changes. Close anyway?')) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        const payload = {
          webhook_verify_token: form.webhook_verify_token,
          display_name:         form.display_name,
        };
        if (form.access_token) payload.access_token = form.access_token;
        res = await axios.put(`/api/integrations/${chatbotId}/${existingChannel.id}`, payload);
        toast.success(`${cfg.label} updated!`);
      } else {
        res = await axios.post(`/api/integrations/${chatbotId}`, { platform, ...form });
        toast.success(`${cfg.label} connected!`);
      }
      onConnected(res.data.channel);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || (isEdit ? 'Failed to update' : 'Failed to connect'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="int-modal-overlay" onClick={handleClose}>
      <div className="int-modal" onClick={e => e.stopPropagation()}>
        <div className="int-modal-header" style={{ borderColor: cfg.color }}>
          <span style={{ fontSize: '1.4rem' }}>{cfg.icon}</span>
          <h2>{isEdit ? `Edit — ${cfg.label}` : `Manual Setup — ${cfg.label}`}</h2>
          <button className="int-modal-close" onClick={handleClose}><XMarkIcon /></button>
        </div>

        <div className="int-manual-note">
          {isEdit ? (
            <>Leave <strong>Access Token</strong> blank to keep the existing token unchanged.</>
          ) : (
            <><strong>Manual setup</strong> requires you to configure webhooks and generate tokens yourself in the
            Meta Developer Console. Use <em>Connect</em> above for the automated one-click flow.</>
          )}
          <br />Webhook URL for this platform:&nbsp;
          <code>{baseUrl}/api/webhooks/{platform}</code>
          <button
            type="button"
            className="btn-copy-inline"
            onClick={() => { navigator.clipboard.writeText(`${baseUrl}/api/webhooks/${platform}`); toast.success('Copied!'); }}
          >Copy</button>
        </div>

        <form onSubmit={handleSubmit} className="int-form">
          <div className="int-field">
            <label>{cfg.channelIdLabel} *</label>
            <input
              type="text"
              value={form.channel_id}
              onChange={e => set('channel_id', e.target.value)}
              placeholder="e.g. 123456789"
              required={!isEdit}
              disabled={isEdit}
              style={isEdit ? { background: '#f1f5f9', cursor: 'not-allowed' } : undefined}
            />
            <small>{isEdit ? 'Channel ID cannot be changed after setup.' : cfg.channelIdHelp}</small>
          </div>
          <div className="int-field">
            <label>{cfg.accessTokenLabel}{isEdit ? '' : ' *'}</label>
            <input
              type="password"
              value={form.access_token}
              onChange={e => set('access_token', e.target.value)}
              placeholder={isEdit ? 'Leave blank to keep existing token' : 'Paste token here'}
              required={!isEdit}
            />
            <small>{cfg.accessTokenHelp}</small>
          </div>
          <div className="int-field">
            <label>Webhook Verify Token *</label>
            <input type="text" value={form.webhook_verify_token} onChange={e => set('webhook_verify_token', e.target.value)} placeholder="e.g. my-secret-abc123" required />
            <small>{cfg.verifyTokenHelp}</small>
          </div>
          <div className="int-field">
            <label>Display Name</label>
            <input type="text" value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="e.g. My Business Page" />
          </div>
          <div className="int-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ background: cfg.color }} disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save Channel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Load Facebook JS SDK once ────────────────────────────────────────────────

function loadFbSdk(appId) {
  return new Promise((resolve, reject) => {
    // Already loaded — re-init with the current appId and resolve immediately
    if (window.FB) {
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: 'v21.0' });
        resolve(window.FB);
      } catch (e) {
        reject(new Error('FB.init failed: ' + e.message));
      }
      return;
    }

    // Timeout — if fbAsyncInit never fires in 10 s the script was blocked or failed
    const timer = setTimeout(() => {
      reject(new Error(
        'Facebook SDK did not load within 10 seconds. ' +
        'Possible causes: (1) browser extension (ad-blocker) blocking connect.facebook.net, ' +
        '(2) Content Security Policy not yet updated on the server, ' +
        '(3) network issue reaching Facebook servers.'
      ));
    }, 10000);

    window.fbAsyncInit = () => {
      clearTimeout(timer);
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: 'v21.0' });
        resolve(window.FB);
      } catch (e) {
        reject(new Error('FB.init failed: ' + e.message));
      }
    };

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error(
        'Failed to load https://connect.facebook.net/en_US/sdk.js — ' +
        'check Content-Security-Policy headers and network connectivity.'
      ));
    };
    document.body.appendChild(script);
  });
}

// ─── Main Integrations page ───────────────────────────────────────────────────

function Integrations() {
  const { id: chatbotId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [chatbot,        setChatbot]        = useState(null);
  const [channels,       setChannels]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [manualPlatform, setManualPlatform] = useState(null);
  const [editChannel,    setEditChannel]    = useState(null);  // channel being edited, or null for new
  const [connecting,     setConnecting]     = useState(null);
  const [metaConfig,     setMetaConfig]     = useState({ app_id: null, wa_config_id: null });
  const [conversations,  setConversations]  = useState([]);
  const [inboxLoading,   setInboxLoading]   = useState(false);

  // WhatsApp Embedded Signup — capture waba_id + phone_number_id from the popup's postMessage
  const waSessionRef = useRef({ waba_id: null, phone_number_id: null });

  // ── Handle ?oauth= redirect params from Facebook callback ────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('oauth');
    if (!status) return;

    if (status === 'success') {
      const saved = parseInt(params.get('saved') || '0', 10);
      toast.success(`✅ Connected! ${saved} channel${saved !== 1 ? 's' : ''} added.`);
      fetchChannels(); // reload
    } else if (status === 'error') {
      toast.error(params.get('msg') || 'Connection failed. Please try again.');
    } else if (status === 'cancelled') {
      toast('Connection cancelled.', { icon: 'ℹ️' });
    }

    // Strip the query param from the URL without a re-render loop
    navigate(location.pathname, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initial data load ─────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([fetchData(), fetchMetaConfig(), fetchConversations()]);
  }, [chatbotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [botRes, chanRes] = await Promise.all([
        axios.get(`/api/chatbots/${chatbotId}`),
        axios.get(`/api/integrations/${chatbotId}`),
      ]);
      setChatbot(botRes.data.chatbot);
      setChannels(chanRes.data.channels);
    } catch {
      toast.error('Failed to load integrations');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`/api/integrations/${chatbotId}`);
      setChannels(res.data.channels);
    } catch { /* non-critical */ }
  };

  const fetchMetaConfig = async () => {
    try {
      const res = await axios.get('/api/settings');
      setMetaConfig({
        app_id:      res.data.settings.meta_app_id      || null,
        wa_config_id: res.data.settings.meta_wa_config_id || null,
      });
    } catch { /* non-critical */ }
  };

  // ── Social inbox (human handover) ────────────────────────────────────────
  const fetchConversations = async () => {
    setInboxLoading(true);
    try {
      const res = await axios.get(`/api/handover/${chatbotId}/conversations`);
      setConversations(res.data.conversations);
    } catch { /* non-critical */ }
    finally { setInboxLoading(false); }
  };

  const handlePause = async (convId) => {
    try {
      await axios.post(`/api/handover/${convId}/pause`);
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, bot_paused: true, bot_paused_by: 'admin' } : c
      ));
      toast.success('Bot paused — you can now reply manually from WhatsApp Business.');
    } catch { toast.error('Failed to pause bot'); }
  };

  const handleResume = async (convId) => {
    try {
      await axios.post(`/api/handover/${convId}/resume`);
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, bot_paused: false } : c
      ));
      toast.success('Bot resumed ✅');
    } catch { toast.error('Failed to resume bot'); }
  };

  // ── Connect handlers ──────────────────────────────────────────────────────

  // Facebook + Instagram: server-side OAuth redirect
  const handleFacebookConnect = async () => {
    if (!metaConfig.app_id) {
      toast.error('META_APP_ID is not configured on the server. Add it to .env and restart.');
      return;
    }
    setConnecting('facebook');
    try {
      const res = await axios.post('/api/oauth/facebook/initiate', { chatbot_id: chatbotId });
      window.location.href = res.data.url; // full-page redirect to Meta
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start Facebook connection.');
      setConnecting(null);
    }
  };

  // WhatsApp: Embedded Signup popup via Facebook JS SDK
  const handleWhatsAppConnect = useCallback(async () => {
    if (!metaConfig.app_id) {
      toast.error('META_APP_ID is not configured on the server. Add it to .env and restart.');
      return;
    }
    if (!metaConfig.wa_config_id) {
      toast.error('META_WA_CONFIG_ID is not configured. Create an Embedded Signup config in Meta App Dashboard → WhatsApp → Embedded Signup, then add it to .env.');
      return;
    }

    setConnecting('whatsapp');

    // Listen for WABA + phone number IDs from the popup postMessage
    const sessionListener = (event) => {
      if (event.origin !== 'https://www.facebook.com') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            waSessionRef.current = {
              waba_id:         data.data.waba_id,
              phone_number_id: data.data.phone_number_id,
            };
          } else if (data.event === 'CANCEL') {
            console.info('[WA Embedded Signup] cancelled at step:', data.data?.current_step);
          } else if (data.event === 'ERROR') {
            console.error('[WA Embedded Signup] error:', data.data?.error_message);
          }
        }
      } catch { /* not JSON — ignore */ }
    };

    window.addEventListener('message', sessionListener);

    try {
      const FB = await loadFbSdk(metaConfig.app_id);

      // FB.login requires a plain (non-async) callback — wrap async work in an IIFE
      FB.login((response) => {
        window.removeEventListener('message', sessionListener);

        (async () => {
          if (!response.authResponse?.code) {
            toast('WhatsApp connection cancelled.', { icon: 'ℹ️' });
            setConnecting(null);
            return;
          }

          const { waba_id, phone_number_id } = waSessionRef.current;
          if (!waba_id || !phone_number_id) {
            toast.error('Embedded Signup did not return a WhatsApp number. Please try again.');
            setConnecting(null);
            return;
          }

          try {
            const res = await axios.post('/api/oauth/whatsapp/exchange', {
              code:           response.authResponse.code,
              waba_id,
              phone_number_id,
              chatbot_id:     chatbotId,
            });
            toast.success('✅ WhatsApp connected!');
            setChannels(prev => {
              const idx = prev.findIndex(c => c.id === res.data.channel.id);
              if (idx >= 0) { const u = [...prev]; u[idx] = res.data.channel; return u; }
              return [res.data.channel, ...prev];
            });
          } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to connect WhatsApp.');
          } finally {
            setConnecting(null);
            waSessionRef.current = { waba_id: null, phone_number_id: null };
          }
        })();
      }, {
        config_id:                    metaConfig.wa_config_id,
        response_type:                'code',
        override_default_response_type: true,
        extras: { sessionInfoVersion: 3, setup: {} },
      });

    } catch (err) {
      window.removeEventListener('message', sessionListener);
      console.error('[WhatsApp Connect] SDK error:', err.message);
      toast.error(err.message || 'Could not load Facebook SDK.', { duration: 8000 });
      setConnecting(null);
    }
  }, [metaConfig, chatbotId]);

  // ── Channel list mutations ─────────────────────────────────────────────────

  const handleToggle = async (channel) => {
    try {
      const res = await axios.patch(`/api/integrations/${chatbotId}/${channel.id}/toggle`);
      setChannels(prev => prev.map(c => c.id === channel.id ? { ...c, is_active: res.data.channel.is_active } : c));
      toast.success(res.data.channel.is_active ? 'Channel enabled' : 'Channel paused');
    } catch {
      toast.error('Failed to update channel');
    }
  };

  const handleDisconnect = async (channel) => {
    if (!window.confirm(`Disconnect this ${PLATFORMS[channel.platform]?.label} channel? The bot will stop responding on it.`)) return;
    try {
      await axios.delete(`/api/integrations/${chatbotId}/${channel.id}`);
      setChannels(prev => prev.filter(c => c.id !== channel.id));
      toast.success('Channel disconnected');
    } catch {
      toast.error('Failed to disconnect channel');
    }
  };

  const handleManualConnected = (newChannel) => {
    setChannels(prev => {
      const idx = prev.findIndex(c => c.id === newChannel.id);
      if (idx >= 0) { const u = [...prev]; u[idx] = newChannel; return u; }
      return [newChannel, ...prev];
    });
  };

  const handleEdit = (channel) => {
    setEditChannel(channel);
    setManualPlatform(channel.platform);
  };

  const handleModalClose = () => {
    setManualPlatform(null);
    setEditChannel(null);
  };

  if (loading) return <div className="loading">Loading…</div>;

  const metaConfigured = !!metaConfig.app_id;

  return (
    <div className="int-page">
      {manualPlatform && (
        <ManualConnectModal
          platform={manualPlatform}
          chatbotId={chatbotId}
          onClose={handleModalClose}
          onConnected={handleManualConnected}
          existingChannel={editChannel}
        />
      )}

      <header className="int-header">
        <div className="container">
          <div className="int-header-inner">
            <button className="back-button" onClick={() => navigate('/dashboard')}>
              <ArrowLeftIcon className="icon" /> Back
            </button>
            <div>
              <h1>Integrations</h1>
              {chatbot && <p className="int-subtitle">{chatbot.name}</p>}
            </div>
          </div>
        </div>
      </header>

      <main className="int-main">
        <div className="container">

          {/* ── Website Embed ── */}
          <section className="int-section">
            <h2 className="int-section-title">🌐 Website Embed</h2>
            <p className="int-section-desc">
              Add this script tag to any website to embed a floating chat widget.
              No approval process or account required.
            </p>
            <div className="int-embed-box">
              <code className="int-embed-code">
                {`<script src="${window.location.origin}/api/embed/${chatbotId}/widget.js" async></script>`}
              </code>
              <button
                className="btn-copy"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `<script src="${window.location.origin}/api/embed/${chatbotId}/widget.js" async></script>`
                  );
                  toast.success('Copied!');
                }}
              >
                Copy
              </button>
            </div>
            <p className="int-embed-hint">
              Also available as a full-page chat at:{' '}
              <a href={`/chat/${chatbotId}`} target="_blank" rel="noreferrer">
                {window.location.origin}/chat/{chatbotId}
              </a>
            </p>
          </section>

          {/* ── Meta not configured banner ── */}
          {!metaConfigured && (
            <div className="int-config-banner">
              <span className="int-config-banner-icon">⚠️</span>
              <div>
                <strong>Meta OAuth not configured</strong>
                <p>
                  Add <code>META_APP_ID</code>, <code>META_APP_SECRET</code>, <code>APP_URL</code>,
                  and <code>META_WA_CONFIG_ID</code> to your <code>.env</code> file to enable
                  one-click Facebook, Instagram, and WhatsApp connections.
                  You can still use <strong>Manual Setup</strong> below.
                </p>
              </div>
            </div>
          )}

          {/* ── Social Channels ── */}
          <section className="int-section">
            <h2 className="int-section-title">📲 Social Channels</h2>
            <p className="int-section-desc">
              Connect your chatbot to Facebook Messenger, Instagram DMs, and WhatsApp Business.
              Your customers keep messaging on their preferred platform — the bot handles them all.
            </p>

            <div className="int-platform-grid">

              {/* ── Facebook Messenger card ── */}
              <PlatformCard
                platform="facebook"
                channels={channels.filter(c => c.platform === 'facebook')}
                metaConfigured={metaConfigured}
                connecting={connecting === 'facebook'}
                onConnect={handleFacebookConnect}
                onManual={() => { setEditChannel(null); setManualPlatform('facebook'); }}
                onToggle={handleToggle}
                onDisconnect={handleDisconnect}
                onEdit={handleEdit}
                connectLabel="Connect with Facebook"
                connectNote="Connects all your Facebook Pages at once"
              />

              {/* ── Instagram DM card ── */}
              <PlatformCard
                platform="instagram"
                channels={channels.filter(c => c.platform === 'instagram')}
                metaConfigured={metaConfigured}
                connecting={connecting === 'facebook'}
                onConnect={handleFacebookConnect}
                onManual={() => { setEditChannel(null); setManualPlatform('instagram'); }}
                onToggle={handleToggle}
                onDisconnect={handleDisconnect}
                onEdit={handleEdit}
                connectLabel="Connect via Facebook"
                connectNote="Instagram DMs are connected automatically when you connect a Facebook Page linked to your Instagram account"
              />

              {/* ── WhatsApp Business card ── */}
              <PlatformCard
                platform="whatsapp"
                channels={channels.filter(c => c.platform === 'whatsapp')}
                metaConfigured={metaConfigured && !!metaConfig.wa_config_id}
                connecting={connecting === 'whatsapp'}
                onConnect={handleWhatsAppConnect}
                onManual={() => { setEditChannel(null); setManualPlatform('whatsapp'); }}
                onToggle={handleToggle}
                onDisconnect={handleDisconnect}
                onEdit={handleEdit}
                connectLabel="Connect WhatsApp Business"
                connectNote={!metaConfig.wa_config_id && metaConfigured ? 'META_WA_CONFIG_ID not set — manual setup only' : 'Walks you through connecting your WhatsApp Business number'}
              />

            </div>
          </section>

          {/* ── Human Handover Inbox ── */}
          {conversations.length > 0 && (
            <section className="int-section">
              <h2 className="int-section-title">🧑‍💼 Social Inbox — Human Handover</h2>
              <p className="int-section-desc">
                When a customer asks for a human or you take over manually, the bot goes silent
                for that thread. Reply from WhatsApp/Facebook Business, then click <strong>Resume Bot</strong> when done.
              </p>

              {inboxLoading ? (
                <p style={{ color: '#6b7280', fontSize: '.9rem' }}>Loading conversations…</p>
              ) : (
                <div className="int-inbox-list">
                  {conversations.map(conv => {
                    const platform = PLATFORMS[conv.platform] || { label: conv.platform, icon: '💬', color: '#6b7280' };
                    const unread = parseInt(conv.unread_count || '0', 10);
                    return (
                      <div key={conv.id} className={`int-inbox-row ${conv.bot_paused ? 'paused' : ''}`}>
                        <div className="int-inbox-platform">
                          <span>{platform.icon}</span>
                        </div>
                        <div className="int-inbox-info">
                          <div className="int-inbox-header">
                            <span className="int-inbox-sender">
                              {conv.platform_sender_id || conv.user_session_id}
                            </span>
                            {unread > 0 && (
                              <span className="int-inbox-unread">{unread} unread</span>
                            )}
                            {conv.bot_paused && (
                              <span className="int-inbox-paused-badge">
                                🤐 Bot paused
                                {conv.bot_paused_by === 'keyword' ? ' (customer requested)' : ' (admin)'}
                              </span>
                            )}
                          </div>
                          {conv.last_message && (
                            <p className="int-inbox-preview">
                              {conv.last_role === 'user' ? '👤 ' : '🤖 '}
                              {conv.last_message.slice(0, 100)}{conv.last_message.length > 100 ? '…' : ''}
                            </p>
                          )}
                          <span className="int-inbox-time">
                            {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <div className="int-inbox-actions">
                          {conv.bot_paused ? (
                            <button
                              className="btn-resume-bot"
                              onClick={() => handleResume(conv.id)}
                            >
                              ▶ Resume Bot
                            </button>
                          ) : (
                            <button
                              className="btn-pause-bot"
                              onClick={() => handlePause(conv.id)}
                            >
                              ⏸ Take Over
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                className="btn-refresh-inbox"
                onClick={fetchConversations}
                style={{ marginTop: '0.75rem', fontSize: '.8rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                🔄 Refresh inbox
              </button>
            </section>
          )}

        </div>
      </main>
    </div>
  );
}

// ─── Reusable platform card ───────────────────────────────────────────────────

function PlatformCard({
  platform, channels, metaConfigured, connecting,
  onConnect, onManual, onToggle, onDisconnect, onEdit,
  connectLabel, connectNote,
}) {
  const cfg = PLATFORMS[platform];
  const [manualOpen, setManualOpen] = useState(false);
  const hasChannels = channels.length > 0;

  return (
    <div
      className="int-platform-card"
      style={{
        borderTopColor: cfg.color,
        background: hasChannels ? cfg.bg : undefined,
      }}
    >
      {/* Header */}
      <div className="int-platform-header">
        <span className="int-platform-icon">{cfg.icon}</span>
        <div>
          <strong className="int-platform-name">{cfg.label}</strong>
          {hasChannels && (
            <span className="int-connected-badge">{channels.length} connected</span>
          )}
        </div>
      </div>

      {/* Primary connect button */}
      <button
        className="btn-oauth-connect"
        style={{ '--platform-color': cfg.color }}
        onClick={onConnect}
        disabled={!metaConfigured || connecting}
      >
        {connecting ? (
          <span className="btn-oauth-spinner" />
        ) : (
          <PlusIcon style={{ width: 15, height: 15 }} />
        )}
        {connecting ? 'Connecting…' : connectLabel}
      </button>

      {connectNote && (
        <p className="int-connect-note">{connectNote}</p>
      )}

      {/* Connected channels list */}
      {hasChannels && (
        <div className="int-channel-list">
          {channels.map(ch => (
            <div key={ch.id} className="int-channel-row">
              <div className="int-channel-info">
                <span className="int-channel-name">{ch.display_name || ch.channel_id}</span>
                <span className="int-channel-id">ID: {ch.channel_id}</span>
              </div>
              <div className="int-channel-actions">
                <button
                  className={`btn-toggle ${ch.is_active ? 'active' : 'paused'}`}
                  onClick={() => onToggle(ch)}
                  title={ch.is_active ? 'Pause' : 'Resume'}
                >
                  {ch.is_active ? 'Active' : 'Paused'}
                </button>
                <button className="btn-icon-edit" title="Edit credentials" onClick={() => onEdit(ch)}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} />
                </button>
                <button className="btn-icon-danger" title="Disconnect" onClick={() => onDisconnect(ch)}>
                  <TrashIcon style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual setup collapsible */}
      <div className="int-manual-toggle">
        <button
          className="int-manual-toggle-btn"
          onClick={() => setManualOpen(o => !o)}
        >
          <ChevronDownIcon
            style={{ width: 13, height: 13, transform: manualOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
          />
          Manual setup
        </button>
        {manualOpen && (
          <div className="int-manual-toggle-content">
            <p>
              Use this if you prefer to configure tokens and webhooks yourself in the Meta Developer Console,
              or if you're in Development Mode and OAuth doesn't work for your account yet.
            </p>
            <button className="btn-manual-open" onClick={onManual}>
              Open manual form →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Integrations;
